import { useEffect, useMemo, useRef, useState } from 'react';
import { Compass, Crosshair, Keyboard } from 'lucide-react';
import { Chrome, type FilterState } from './components/Chrome';
import { FloatingWindow } from './components/FloatingWindow';
import { KeyboardShortcuts } from './components/KeyboardShortcuts';
import { KosmosScene } from './components/KosmosScene';
import { OrientationMap } from './components/OrientationMap';
import { usePreferences } from './hooks/usePreferences';
import { useVaultKosmos } from './hooks/useVaultKosmos';
import { capturePresetFromLocation, trailerCues, type TrailerCue } from './lib/capture-mode';
import { layoutGraph } from './lib/graph-layout';
import type { VaultNode } from '../shared/types';

export type NavigationMode = 'overview' | 'focus' | 'deep' | 'fly';
export interface CameraFlight {
  id: number;
  mode: CameraFlightMode;
}

export type CameraFlightMode = Exclude<NavigationMode, 'fly'>;

const initialFilters: FilterState = {
  query: '',
  areas: [],
  tags: [],
  statuses: [],
  types: [],
  showUnresolved: true
};

const NAVIGATION_SHORTCUTS: Partial<Record<string, NavigationMode>> = {
  KeyA: 'overview',
  KeyS: 'focus',
  KeyD: 'deep',
  KeyF: 'fly'
};

export interface GrowthState {
  enabled: boolean;
  playing: boolean;
  progress: number;
  speed: number;
  focusZoom: boolean;
}

function isTextInputTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return Boolean(target.closest('input, textarea, select, [contenteditable="true"]'));
}

export function App() {
  const capturePreset = useMemo(() => capturePresetFromLocation(), []);
  const {
    graph,
    events,
    connection,
    configState,
    configError,
    configSaving,
    vaultDialogOpen,
    liveNodeIds,
    demoMode,
    updateVaultPath,
    chooseVaultWithDialog
  } = useVaultKosmos();
  const { language, setLanguage, toggleLanguage, t } = usePreferences();
  const [selectedNodeId, setSelectedNodeId] = useState<string | undefined>(capturePreset?.selectedNodeId);
  const [hoveredNodeId, setHoveredNodeId] = useState<string | undefined>();
  const [navigationMode, setNavigationMode] = useState<NavigationMode>(capturePreset?.navigationMode ?? 'overview');
  const [cameraFlight, setCameraFlight] = useState<CameraFlight>({
    id: 0,
    mode: capturePreset?.navigationMode === 'fly' ? 'overview' : capturePreset?.navigationMode ?? 'overview'
  });
  const [historyPlaying, setHistoryPlaying] = useState(false);
  const [, setHistoryCursor] = useState(0);
  const [growth, setGrowth] = useState<GrowthState>(capturePreset?.growth ?? { enabled: false, playing: false, progress: 1, speed: 1, focusZoom: false });
  const [highlightsEnabled, setHighlightsEnabled] = useState(capturePreset?.highlightsEnabled ?? true);
  const [labelsEnabled, setLabelsEnabled] = useState(capturePreset?.labelsEnabled ?? true);
  const [filters, setFilters] = useState<FilterState>(initialFilters);
  const [activeTrailerCue, setActiveTrailerCue] = useState<TrailerCue | undefined>(capturePreset?.scriptedTrailer ? trailerCues[0] : undefined);
  const lastGrowthFocusIndex = useRef(-1);
  const captureApplied = useRef(false);
  const lastTrailerCueIndex = useRef(-1);

  function flyTo(mode: NavigationMode) {
    setNavigationMode(mode);
    if (mode === 'fly') return;
    setCameraFlight((current) => ({ id: current.id + 1, mode }));
  }

  function clearFocus() {
    setHighlightsEnabled(false);
    setSelectedNodeId(undefined);
    setHoveredNodeId(undefined);
    setHistoryPlaying(false);
    setGrowth((current) => ({ ...current, focusZoom: false }));
  }

  function toggleLabels() {
    setLabelsEnabled((enabled) => !enabled);
  }

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.repeat || event.altKey || event.ctrlKey || event.metaKey) return;
      if (isTextInputTarget(event.target)) return;

      if (event.code === 'KeyQ') {
        event.preventDefault();
        clearFocus();
        return;
      }

      if (event.code === 'KeyR') {
        event.preventDefault();
        toggleLabels();
        return;
      }

      if (navigationMode === 'fly') {
        if (event.code === 'KeyF') {
          event.preventDefault();
          flyTo('overview');
        }
        return;
      }

      const nextMode = NAVIGATION_SHORTCUTS[event.code];
      if (!nextMode) return;
      event.preventDefault();
      flyTo(nextMode);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [navigationMode]);

  useEffect(() => {
    if (!capturePreset) return;
    setLanguage(capturePreset.language);
  }, [capturePreset, setLanguage]);

  useEffect(() => {
    if (!capturePreset || !graph || captureApplied.current) return;
    captureApplied.current = true;
    setSelectedNodeId(capturePreset.selectedNodeId);
    setGrowth(capturePreset.growth);
    setHighlightsEnabled(capturePreset.highlightsEnabled);
    setLabelsEnabled(capturePreset.labelsEnabled);
    flyTo(capturePreset.navigationMode);
  }, [capturePreset, graph]);

  useEffect(() => {
    if (!capturePreset?.scriptedTrailer || !graph) return;
    const startedAt = performance.now();

    function applyCue(index: number) {
      const cue = trailerCues[index];
      if (!cue || lastTrailerCueIndex.current === index) return;
      lastTrailerCueIndex.current = index;
      setActiveTrailerCue(cue);
      setHighlightsEnabled(true);
      setLabelsEnabled(true);
      setSelectedNodeId(cue.nodeId);
      setHoveredNodeId(undefined);
      setHistoryPlaying(false);
      setGrowth(cue.growth ?? { enabled: false, playing: false, progress: 1, speed: 1, focusZoom: false });
      const nextMode = cue.navigationMode;
      setNavigationMode(nextMode);
      if (nextMode !== 'fly') {
        setCameraFlight((current) => ({ id: current.id + 1, mode: nextMode }));
      }
    }

    applyCue(0);
    const timer = window.setInterval(() => {
      const elapsed = (performance.now() - startedAt) % (capturePreset.durationMs ?? trailerCues.at(-1)?.endMs ?? 28000);
      const nextIndex = Math.max(0, trailerCues.findIndex((cue) => elapsed >= cue.startMs && elapsed < cue.endMs));
      applyCue(nextIndex);
    }, 160);

    return () => window.clearInterval(timer);
  }, [capturePreset, graph]);

  const effectiveFilters = useMemo(() => {
    if (!graph) return filters;
    return {
      ...filters,
      areas: filters.areas.length === 0 ? graph.areas.filter((area) => area !== 'Vault') : filters.areas
    };
  }, [filters, graph]);

  const filteredGraph = useMemo(() => {
    if (!graph) return null;
    const query = effectiveFilters.query.trim().toLowerCase();
    const activeAreas = new Set(effectiveFilters.areas);
    const nodes = graph.nodes.filter((node) => {
      if (node.area !== 'Vault' && !activeAreas.has(node.area)) return false;
      if (!effectiveFilters.showUnresolved && node.kind === 'unresolved') return false;
      if (effectiveFilters.tags.length > 0 && !effectiveFilters.tags.some((tag) => node.tags.includes(tag))) return false;
      if (effectiveFilters.statuses.length > 0 && (!node.status || !effectiveFilters.statuses.includes(node.status))) return false;
      if (effectiveFilters.types.length > 0 && (!node.type || !effectiveFilters.types.includes(node.type))) return false;
      if (!query) return true;
      return [node.label, node.path, node.area, node.type, node.status, ...node.tags]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(query);
    });

    const nodeIds = new Set(nodes.map((node) => node.id));
    return {
      ...graph,
      nodes,
      links: graph.links.filter((link) => nodeIds.has(link.source) && nodeIds.has(link.target))
    };
  }, [effectiveFilters, graph]);

  const chronology = useMemo(() => {
    if (!filteredGraph) return [];
    return filteredGraph.nodes
      .filter((node) => node.kind === 'file')
      .map((node) => ({
        node,
        time: Date.parse(node.createdAt ?? node.updatedAt ?? '')
      }))
      .sort((a, b) => {
        const timeA = Number.isFinite(a.time) ? a.time : Number.MAX_SAFE_INTEGER;
        const timeB = Number.isFinite(b.time) ? b.time : Number.MAX_SAFE_INTEGER;
        return timeA - timeB || a.node.path.localeCompare(b.node.path, 'de');
      });
  }, [filteredGraph]);

  const growthGraph = useMemo(() => {
    if (!filteredGraph || !growth.enabled || chronology.length === 0) return filteredGraph;

    const visibleFileCount = Math.max(1, Math.ceil(chronology.length * growth.progress));
    const visibleFileIds = new Set(chronology.slice(0, visibleFileCount).map((entry) => entry.node.id));
    const visibleFolderIds = new Set<string>(['folder:.']);

    for (const node of filteredGraph.nodes) {
      if (node.kind !== 'folder') continue;
      if (
        [...visibleFileIds].some((fileId) => {
          const filePath = fileId.slice('file:'.length);
          return node.path === '' || filePath.startsWith(`${node.path}/`);
        })
      ) {
        visibleFolderIds.add(node.id);
      }
    }

    const visibleNodeIds = new Set([...visibleFileIds, ...visibleFolderIds]);
    const links = filteredGraph.links.filter((link) => visibleNodeIds.has(link.source) && visibleNodeIds.has(link.target));
    const nodes = filteredGraph.nodes.filter((node) => visibleNodeIds.has(node.id));

    return {
      ...filteredGraph,
      nodes,
      links
    };
  }, [chronology, filteredGraph, growth.enabled, growth.progress]);

  const emergingNodeIds = useMemo(() => {
    if (!growth.enabled || chronology.length === 0) return new Set<string>();
    const visibleFileCount = Math.max(1, Math.ceil(chronology.length * growth.progress));
    return new Set(chronology.slice(Math.max(0, visibleFileCount - 18), visibleFileCount).map((entry) => entry.node.id));
  }, [chronology, growth.enabled, growth.progress]);

  const primaryLiveNodeId = useMemo(() => {
    const latestActiveEvent = [...events]
      .reverse()
      .find((event) => event.path && event.path !== '.' && event.type !== 'ready');
    return latestActiveEvent ? `file:${latestActiveEvent.path}` : undefined;
  }, [events]);

  const effectiveLiveNodeIds = useMemo(
    () => (highlightsEnabled ? liveNodeIds : new Set<string>()),
    [highlightsEnabled, liveNodeIds]
  );
  const effectivePrimaryLiveNodeId = highlightsEnabled ? primaryLiveNodeId : undefined;

  const positionedGraph = useMemo(
    () => (growthGraph ? layoutGraph(growthGraph, effectiveLiveNodeIds, effectivePrimaryLiveNodeId) : null),
    [effectiveLiveNodeIds, effectivePrimaryLiveNodeId, growthGraph]
  );

  const selectedNode = useMemo(
    () => (highlightsEnabled ? graph?.nodes.find((node) => node.id === selectedNodeId) : undefined),
    [graph, highlightsEnabled, selectedNodeId]
  );

  const backlinks = useMemo(() => {
    if (!graph || !selectedNodeId) return [];
    const sourceIds = graph.links
      .filter((link) => link.kind !== 'contains' && link.target === selectedNodeId)
      .map((link) => link.source);
    const byId = new Map(graph.nodes.map((node) => [node.id, node]));
    return sourceIds.map((id) => byId.get(id)).filter(Boolean) as VaultNode[];
  }, [graph, selectedNodeId]);

  const developmentEvents = useMemo(
    () => events.filter((event) => event.path && event.path !== '.' && event.type !== 'ready').slice(-24),
    [events]
  );

  useEffect(() => {
    if (!historyPlaying || developmentEvents.length === 0) return;

    const timer = window.setInterval(() => {
      setHistoryCursor((current) => {
        const next = (current + 1) % developmentEvents.length;
        const event = developmentEvents[next];
        if (growth.focusZoom) {
          setHighlightsEnabled(true);
          setSelectedNodeId(`file:${event.path}`);
          flyTo(event.type === 'add' ? 'deep' : 'focus');
        }
        return next;
      });
    }, 1700);

    return () => window.clearInterval(timer);
  }, [developmentEvents, growth.focusZoom, historyPlaying]);

  useEffect(() => {
    if (!growth.playing || !growth.enabled || chronology.length === 0) return;

    const timer = window.setInterval(() => {
      setGrowth((current) => {
        const speed = Math.max(0.2, Math.min(4, current.speed));
        const nextProgress = Math.min(1, current.progress + Math.max(0.0025, (7 * speed) / chronology.length));
        return {
          ...current,
          playing: nextProgress < 1,
          progress: nextProgress
        };
      });
    }, 260);

    return () => window.clearInterval(timer);
  }, [chronology.length, growth.enabled, growth.playing]);

  useEffect(() => {
    if (!growth.enabled || !growth.focusZoom || chronology.length === 0) {
      lastGrowthFocusIndex.current = -1;
      return;
    }

    const visibleIndex = Math.max(0, Math.ceil(chronology.length * growth.progress) - 1);
    const cadence = Math.max(8, Math.round(chronology.length / 80));
    if (visibleIndex < 1 || Math.abs(visibleIndex - lastGrowthFocusIndex.current) < cadence) return;

    const nodeId = chronology[visibleIndex]?.node.id;
    if (!nodeId) return;
    lastGrowthFocusIndex.current = visibleIndex;
    setHighlightsEnabled(true);
    setSelectedNodeId(nodeId);
    flyTo('focus');
  }, [chronology, growth.enabled, growth.focusZoom, growth.progress]);

  if (!graph || !positionedGraph) {
    return (
      <main className="loading-screen">
        <div className="loader-ring" />
        <h1>{t('appTitle')}</h1>
        <p>{configError ?? t('indexBuilding')}</p>
      </main>
    );
  }

  return (
    <main className={capturePreset ? `app-shell capture-mode capture-${capturePreset.id}` : 'app-shell'}>
      <KosmosScene
        graph={positionedGraph}
        selectedNodeId={highlightsEnabled ? selectedNodeId : undefined}
        hoveredNodeId={hoveredNodeId}
        liveNodeIds={effectiveLiveNodeIds}
        primaryLiveNodeId={effectivePrimaryLiveNodeId}
        navigationMode={navigationMode}
        cameraFlight={cameraFlight}
        emergingNodeIds={emergingNodeIds}
        labelsEnabled={labelsEnabled}
        onSelectNode={(nodeId) => {
          setHighlightsEnabled(true);
          setSelectedNodeId(nodeId);
        }}
        onHoverNode={setHoveredNodeId}
      />
      <FloatingWindow
        id="orientation"
        title={t('map')}
        icon={<Compass size={15} />}
        defaultRect={{
          x: Math.max(356, window.innerWidth - 348),
          y: Math.max(460, window.innerHeight - 380),
          width: 330,
          height: 250
        }}
        minWidth={292}
        minHeight={224}
        className="orientation-window"
        forcedCollapsed={activeTrailerCue ? activeTrailerCue.window !== 'orientation' : undefined}
        emphasized={activeTrailerCue?.window === 'orientation'}
        toolbar={
          <button type="button" onClick={() => flyTo('overview')} title={t('keyOverviewDesc')}>
            <Crosshair size={14} />
          </button>
        }
      >
        <OrientationMap
          graph={positionedGraph}
          selectedNodeId={highlightsEnabled ? selectedNodeId : undefined}
          hoveredNodeId={hoveredNodeId}
          liveNodeIds={effectiveLiveNodeIds}
          primaryLiveNodeId={effectivePrimaryLiveNodeId}
          emergingNodeIds={emergingNodeIds}
          navigationMode={navigationMode}
          onSelectNode={(nodeId) => {
            setHighlightsEnabled(true);
            setSelectedNodeId(nodeId);
            flyTo('focus');
          }}
        />
      </FloatingWindow>
      <Chrome
        graph={graph}
        events={events}
        connection={connection}
        filters={effectiveFilters}
        selectedNode={selectedNode}
        backlinks={backlinks}
        configState={configState}
        configError={configError}
        configSaving={configSaving}
        vaultDialogOpen={vaultDialogOpen}
        demoMode={demoMode}
        language={language}
        t={t}
        navigationMode={navigationMode}
        primaryLiveNodeId={primaryLiveNodeId}
        historyPlaying={historyPlaying}
        growth={growth}
        highlightsEnabled={highlightsEnabled}
        labelsEnabled={labelsEnabled}
        trailerActiveWindow={activeTrailerCue?.window}
        growthTotal={chronology.length}
        growthVisible={growth.enabled ? positionedGraph.nodes.filter((node) => node.kind === 'file').length : chronology.length}
        onFiltersChange={setFilters}
        onSelectNode={(nodeId) => {
          setHighlightsEnabled(true);
          setSelectedNodeId(nodeId);
          flyTo('focus');
        }}
        onVaultPathSubmit={updateVaultPath}
        onVaultDialog={chooseVaultWithDialog}
        onLanguageToggle={toggleLanguage}
        onNavigationModeChange={flyTo}
        onGrowthChange={setGrowth}
        onLabelsEnabledChange={setLabelsEnabled}
        onToggleHistory={() => setHistoryPlaying((playing) => !playing)}
        onFocusEvent={(event) => {
          setHistoryPlaying(false);
          setHighlightsEnabled(true);
          setSelectedNodeId(`file:${event.path}`);
          flyTo(event.type === 'add' ? 'deep' : 'focus');
        }}
        onFocusLive={() => {
          if (primaryLiveNodeId) {
            setHighlightsEnabled(true);
            setSelectedNodeId(primaryLiveNodeId);
            flyTo('focus');
          }
        }}
        onClearFocus={clearFocus}
      />
      {activeTrailerCue && <TrailerCueOverlay cue={activeTrailerCue} />}
      {!capturePreset?.hideKeyboard && (
        <FloatingWindow
          id="keyboard"
          title={t('keyboard')}
          icon={<Keyboard size={15} />}
          defaultRect={{
            x: Math.max(12, (window.innerWidth - 580) / 2),
            y: Math.max(230, window.innerHeight - 252),
            width: Math.min(580, Math.max(320, window.innerWidth - 24)),
            height: 240
          }}
          minWidth={320}
          minHeight={230}
          className="keyboard-window"
          placement="bottom-center"
        >
          <KeyboardShortcuts
            t={t}
            navigationMode={navigationMode}
            labelsEnabled={labelsEnabled}
            onNavigationModeChange={flyTo}
            onClearFocus={clearFocus}
            onToggleLabels={toggleLabels}
          />
        </FloatingWindow>
      )}
    </main>
  );
}

function TrailerCueOverlay({ cue }: { cue: TrailerCue }) {
  return (
    <aside className="trailer-cue" aria-live="polite">
      <span>{cue.kicker}</span>
      <h1>{cue.title}</h1>
      <p>{cue.copy}</p>
    </aside>
  );
}
